import { Component, EventEmitter, OnInit, Output, ViewChild } from '@angular/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { QuotationFormDialogComponent } from '../quotation-form-dialog/quotation-form-dialog.component';
import { QuotationStatusDialogComponent } from '../quotation-status-dialog/quotation-status-dialog.component';
import { Table } from 'primeng/table';
import * as XLSX from 'xlsx';
import { WorkflowDataService } from '../../services/workflow-data.service';
import { forkJoin } from 'rxjs/internal/observable/forkJoin';
import { v4 as uuidv4 } from 'uuid';
import { AuthService } from '../../services/auth.service';
import { OverlayPanel } from 'primeng/overlaypanel';
import jsPDF from 'jspdf';

interface User {
  id: number;
  name: string;
  email: string;
  employeenumber: string;
  role: string;
  createdat: string;
  updatedat: string;
}
export interface ConflictItem {
  quotationNo: string;
  existingData: any;
  newData: any;
  conflicts: string[];
  selected: boolean;
}
@Component({
  selector: 'app-quotation-list',
  templateUrl: './quotation-list.component.html',
  styleUrls: ['./quotation-list.component.scss'],
  providers: [DialogService, ConfirmationService]
})
export class QuotationListComponent implements OnInit {
  @ViewChild('dt') dt!: Table;
  @ViewChild('opCustomer') opCustomer!: OverlayPanel;
  @ViewChild('opProject') opProject!: OverlayPanel;
  @ViewChild('opPoNumber') opPoNumber!: OverlayPanel;
  @ViewChild('opInvoices') opInvoices!: OverlayPanel;
  @ViewChild('opPayments') opPayments!: OverlayPanel;
  isHeaderCollapsed = false;
  workflowItems: any[] = [];
  ref!: DynamicDialogRef;
  uploadedFileName: string | null = null;
  selectedWorkflowItems: any[] = []; // Add this to store selected rows
  uploadProgress = 0;
  showUploadDialog = false;
  uploadStatus = '';
  isUploading = false;
  dateRange: Date[] = [];
  isClearing = false;
  filteredWorkflowItems: any[] = []; // New property for filtered data
  userDetails: User | null = null;
  loading = true; // Main loading state
  tabLoadingStates = {
    all: true,
    quotations: true,
    po: true,
    pi: true,
    workOrder: true,
    ci: true,
    payment: true
  };
  paymentStatusOptions = [
    { label: 'Completed', value: 'Completed' },
    { label: 'Pending', value: 'Pending' }
  ];
  private readonly pdfColors = {
    primary: [13, 36, 56],           // Professional blue #0d2438
    secondary: [52, 73, 94],         // Dark gray-blue
    success: [39, 174, 96],          // Green
    warning: [243, 156, 18],         // Orange
    danger: [231, 76, 60],           // Red
    light: [248, 249, 250],          // Light gray
    white: [255, 255, 255],          // White
    text: [33, 37, 41],              // Dark text
    accent: [52, 152, 219],          // Blue accent
    tableHeader: [236, 240, 241],    // Table header
    border: [189, 195, 199]          // Border color
  };
  showConflictDialog = false;
  conflictItems: ConflictItem[] = [];
  isProcessingConflicts = false;
  uploadError = '';
  selectAllConflicts = false;
  isGeneratingPDF = false;
  isGeneratingExcel: boolean = false;
  private readonly PAGE_BOTTOM_BUFFER = 30;
  selectedFilter: string = 'all';
  filterOptions = [
    { label: 'All', value: 'all' },
    { label: 'Encloser', value: 'encloser' },
    { label: 'Drawing', value: 'drawing' },
    { label: 'Lost Orders', value: 'lost-orders' }, // Add this
  ];
  dateFilterOptions = [
    { label: 'Quotation Date', value: 'quotationDate' },
    { label: 'Payment Date', value: 'paymentDate' },
    { label: 'Invoice Date', value: 'invoiceDate' },
  ];

  showClearConfirmDialog: boolean = false;
  clearConfirmPassword: string = '';
  clearPasswordError: string = '';
  clearSuccess: boolean = false;
  clearError: string = '';

  selectedDateFilterType: string = 'quotationDate';

  constructor(
    private dialogService: DialogService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private workflowDataService: WorkflowDataService,
    private authService: AuthService
  ) { }

  ngOnInit(): void {
    this.loading = true;
    this.loadWorkflowItems();

    this.workflowDataService.workflowItems$.subscribe(items => {
      console.log('items', items);

      // Store ALL items for export (including those without poNumber)
      this.workflowItems = items.map(item => ({
        ...this.getDefaultWorkflowData(),
        ...item.data,
        orderId: item.orderId || `ORD-${Math.random().toString(36).substr(2, 9)}`,
        currentStep: item.currentStep,
        completed: item.completed,
        paymentReceivedDate: this.processPaymentDate(item.data.paymentReceivedDate),
        uuid: item.uuid,
        status: item.data.status,
        statusId: item.data.statusId,
        showEmail: false,
        showFullProjectName: false,
        showFullCustomerName: false,
        showFullRemarks: false,
        // Handle multiple invoices display
        displayInvoices: this.formatInvoicesForDisplay(item.data.invoices),
        // Handle multiple payments display  
        displayPayments: this.formatPaymentsForDisplay(item.data.payments),
        // Calculate totals for display
        totalInvoiceAmount: this.calculateTotalInvoiceAmount(item.data.invoices),
        totalPaymentAmount: this.calculateTotalPaymentAmount(item.data.payments),
        outstandingAmount: this.calculateOutstandingAmount(item.data.invoices, item.data.payments),
        // Add payment completion status
        isPaymentCompleted: this.isPaymentCompleted(item.data),
        paymentStatusText: this.isPaymentCompleted(item.data) ? 'Completed' : 'Pending',
      }));

      console.log('Mapped workflowItems with status:', this.workflowItems.map(w => w.status));
      console.log('Workflow items loaded:', this.workflowItems);

      // Sort by quotationDate in descending order (newest first)
      this.workflowItems.sort((a, b) => {
        const dateA = a.quotationDate ? new Date(a.quotationDate).getTime() : 0;
        const dateB = b.quotationDate ? new Date(b.quotationDate).getTime() : 0;
        return dateB - dateA;
      });

      // Sort all items for export as well
      this.workflowItems.sort((a, b) => {
        const dateA = a.quotationDate ? new Date(a.quotationDate).getTime() : 0;
        const dateB = b.quotationDate ? new Date(b.quotationDate).getTime() : 0;
        return dateB - dateA;
      });

      this.filteredWorkflowItems = [...this.workflowItems];
      this.applyFilter();
      this.loading = false;
      this.resetTabLoadingStates();
    });

    this.userDetails = this.authService.getCurrentUser();
  }
  onFilterChange(event: any): void {
    this.selectedFilter = event.value;
    this.applyFilter();
  }

  applyFilter(): void {
    switch (this.selectedFilter) {
      case 'encloser':
        this.filteredWorkflowItems = this.workflowItems.filter(item =>
          item.jobNumber && item.jobNumber.toString().toUpperCase().startsWith('KBX')
        );
        break;
      case 'drawing':
        this.filteredWorkflowItems = this.workflowItems.filter(item =>
          item.jobNumber && item.jobNumber.toString().toUpperCase().startsWith('DRW')
        );
        break;
      case 'lost-orders':
        this.filteredWorkflowItems = this.workflowItems.filter(item =>
          !item.poNumber ||
          item.poNumber === null ||
          item.poNumber === undefined ||
          item.poNumber === '' ||
          item.poNumber.toString().trim() === '' ||
          !item.jobNumber ||
          item.jobNumber === null ||
          item.jobNumber === undefined ||
          item.jobNumber === '' ||
          item.jobNumber.toString().trim() === ''
        );
        break;
      case 'all':
      default:
        this.filteredWorkflowItems = [...this.workflowItems];
        break;
    }
    console.log(`Filtered items (${this.selectedFilter}):`, this.filteredWorkflowItems.length);
  }


  async generatePDF(): Promise<void> {
    if (!this.selectedWorkflowItems || this.selectedWorkflowItems.length === 0) {
      alert('Please select at least one item to generate PDF');
      return;
    }

    this.isGeneratingPDF = true;

    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      let isFirstPage = true;

      for (const item of this.selectedWorkflowItems) {
        if (!isFirstPage) {
          pdf.addPage();
        }

        await this.addEnhancedItemToPDF(pdf, item);
        isFirstPage = false;
      }

      // Save the PDF with better filename
      const fileName = `KIKBLOX_Workflow_Report_${new Date().toISOString().split('T')[0]}_${new Date().getTime()}.pdf`;
      pdf.save(fileName);

    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    } finally {
      this.isGeneratingPDF = false;
    }
  }

  private async addEnhancedItemToPDF(pdf: jsPDF, item: any): Promise<void> {
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 20;
    let yPosition = margin;

    // Use the class-level colors
    const colors = this.pdfColors;

    // Add professional header
    yPosition = await this.addProfessionalHeader(pdf, item, yPosition, colors, pageWidth, margin);
    yPosition += 15;

    // Client Information Section
    yPosition = await this.checkSpaceAndAddPage(pdf, yPosition, 60, margin, item, colors, pageWidth);
    yPosition = this.addSectionTitle(pdf, 'CLIENT INFORMATION', yPosition, colors, pageWidth, margin);

    const clientInfo = [
      ['Customer Name', item.customerName || 'N/A'],
      ['Email Address', item.email || 'N/A'],
      ['Country', item.country || 'N/A'],
      ['Address', this.wrapText(item.customerAddress || 'N/A', 50)],
      ['Project Name', this.wrapText(item.project || 'N/A', 50)],
      ['PO Number', item.poNumber || 'N/A'],
      ['Quotation Date', item.quotationDate ? new Date(item.quotationDate).toLocaleDateString() : 'N/A'],
      ['Method of Inquiry', item.methodOfInquiryReceived || 'N/A']
    ];

    yPosition = this.addProfessionalTable(pdf, clientInfo, yPosition, colors, pageWidth, margin, false);
    yPosition += 10;

    // Financial Overview Section
    yPosition = await this.checkSpaceAndAddPage(pdf, yPosition, 40, margin, item, colors, pageWidth);
    yPosition = this.addSectionTitle(pdf, 'FINANCIAL OVERVIEW', yPosition, colors, pageWidth, margin);

    const totalInvoiceAmount = this.calculateTotalInvoiceAmount(item.invoices || []);
    const totalPaymentAmount = this.calculateTotalPaymentAmount(item.payments || []);
    const totalPrice = parseFloat(item.totalPrice) || 0;

    // **CORRECTED OUTSTANDING CALCULATION**
    let outstandingAmount = 0;
    let isCompleted = false;

    if (totalInvoiceAmount > 0) {
      // If invoices exist, calculate outstanding based on invoices vs payments
      outstandingAmount = totalInvoiceAmount - totalPaymentAmount;
      isCompleted = outstandingAmount <= 0;
    } else if (totalPrice > 0) {
      // If no invoices but project value exists, calculate outstanding based on project value vs payments
      outstandingAmount = totalPrice - totalPaymentAmount;
      isCompleted = outstandingAmount <= 0;
    } else {
      // No project value or invoices
      outstandingAmount = 0;
      isCompleted = true;
    }

    // Ensure outstanding amount is not negative
    outstandingAmount = Math.max(0, outstandingAmount);

    // Add financial cards
    yPosition = this.addFinancialSummaryCards(pdf, {
      totalInvoice: totalInvoiceAmount,
      totalPayment: totalPaymentAmount,
      outstanding: outstandingAmount,
      totalPrice: totalPrice,
      isCompleted: isCompleted
    }, yPosition, colors, pageWidth, margin);

    yPosition += 8;

    // **Check space for Payment Progress section**
    const paymentProgressHeight = 35;
    const availableSpace = pageHeight - yPosition - 30;

    if (availableSpace >= paymentProgressHeight) {
      // Add Payment Progress on same page
      yPosition = this.addSectionTitle(pdf, 'PAYMENT PROGRESS', yPosition, colors, pageWidth, margin);
      yPosition = this.addPaymentProgressSection(pdf, item, yPosition, colors, pageWidth, margin);
      yPosition += 1; // Minimal gap

      // Check space for status summary
      const statusSummaryHeight = 20;
      const remainingSpace = pageHeight - yPosition - 30;

      if (remainingSpace >= statusSummaryHeight) {
        // Add Status Summary on same page with minimal gap
        yPosition = this.addStatusSummary(pdf, item, outstandingAmount, yPosition, colors, pageWidth, margin);
        yPosition += 15;
      }
    }

    // Continue with other sections...
    // Invoice Details Section
    if (item.invoices && item.invoices.length > 0) {
      const invoiceTableHeight = 40 + (item.invoices.length * 8);
      yPosition = await this.checkSpaceAndAddPage(pdf, yPosition, invoiceTableHeight, margin, item, colors, pageWidth);

      yPosition = this.addSectionTitle(pdf, 'INVOICE DETAILS', yPosition, colors, pageWidth, margin);

      const invoiceHeaders = ['Invoice No.', 'Date', 'Amount', 'Due Date'];
      const invoiceData = item.invoices.map((invoice: any) => [
        invoice.invoiceNo || 'N/A',
        invoice.date || 'N/A',
        `$${(invoice.invoicePrice || 0).toFixed(2)}`,
        invoice.paymentDueDate || 'N/A'
      ]);

      yPosition = this.addDataTable(pdf, invoiceHeaders, invoiceData, yPosition, colors, pageWidth, margin);
      yPosition += 10;
    }

    // Payment Details Section
    if (item.payments && item.payments.length > 0) {
      const paymentTableHeight = 40 + (item.payments.length * 8);
      yPosition = await this.checkSpaceAndAddPage(pdf, yPosition, paymentTableHeight, margin, item, colors, pageWidth);

      yPosition = this.addSectionTitle(pdf, 'PAYMENT DETAILS', yPosition, colors, pageWidth, margin);

      const paymentHeaders = ['Invoice No.', 'Payment Date', 'Amount', 'Status'];
      const paymentData = item.payments.map((payment: any) => [
        payment.invoiceNo || 'Update Invoice No',
        payment.paymentReceivedDate || 'N/A',
        `$${(payment.paymentPrice || 0).toFixed(2)}`,
        payment.paymentReceived ? 'RECEIVED' : 'PENDING'
      ]);

      yPosition = this.addDataTable(pdf, paymentHeaders, paymentData, yPosition, colors, pageWidth, margin);
      yPosition += 10;
    }

    // If Payment Progress wasn't added above, add it here
    const wasPaymentProgressAdded = availableSpace >= paymentProgressHeight;
    if (!wasPaymentProgressAdded) {
      yPosition = await this.checkSpaceAndAddPage(pdf, yPosition, 60, margin, item, colors, pageWidth);
      yPosition = this.addSectionTitle(pdf, 'PAYMENT PROGRESS', yPosition, colors, pageWidth, margin);
      yPosition = this.addPaymentProgressSection(pdf, item, yPosition, colors, pageWidth, margin);

      // Status Summary with minimal gap
      yPosition = await this.checkSpaceAndAddPage(pdf, yPosition, 50, margin, item, colors, pageWidth);
      yPosition = this.addStatusSummary(pdf, item, outstandingAmount, yPosition, colors, pageWidth, margin);
    }

    // Add footer
    this.addProfessionalFooter(pdf, item, colors, pageWidth, pageHeight, margin);
  }

  // Professional Header with standardized dimensions
  private async addProfessionalHeader(pdf: jsPDF, item: any, yPosition: number, colors: any, pageWidth: number, margin: number): Promise<number> {
    const headerHeight = 45;

    // Header background
    pdf.setFillColor(colors.primary[0], colors.primary[1], colors.primary[2]);
    pdf.rect(0, 0, pageWidth, headerHeight, 'F');

    // Top accent line
    pdf.setFillColor(colors.accent[0], colors.accent[1], colors.accent[2]);
    pdf.rect(0, 0, pageWidth, 3, 'F');

    // Logo section (standardized size)
    try {
      const logoImg = await this.loadImage('assets/img/kikblox_logo.webp');
      pdf.addImage(logoImg, 'PNG', margin, 8, 30, 15);
    } catch (error) {
      console.warn('Logo not found, continuing without logo');
    }

    // Company branding (right aligned, standardized)
    pdf.setTextColor(colors.white[0], colors.white[1], colors.white[2]);
    pdf.setFontSize(16);
    pdf.setFont('helvetica', 'bold');
    pdf.text('KIKBLOX', pageWidth - margin - 45, 16);

    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'normal');
    pdf.text('Quotation Management', pageWidth - margin - 45, 22);
    pdf.text('Quotation Analysis Report', pageWidth - margin - 45, 27);

    // Document title
    yPosition = headerHeight + 10;
    pdf.setTextColor(colors.primary[0], colors.primary[1], colors.primary[2]);
    pdf.setFontSize(20);
    pdf.setFont('helvetica', 'bold');
    pdf.text('QUOTATION REPORT', margin, yPosition);

    // Title underline
    pdf.setDrawColor(colors.primary[0], colors.primary[1], colors.primary[2]);
    pdf.setLineWidth(2);
    pdf.line(margin, yPosition + 2, margin + 75, yPosition + 2);

    yPosition += 8;

    // Document metadata (standardized info box)
    const metadataHeight = 16;
    pdf.setFillColor(colors.light[0], colors.light[1], colors.light[2]);
    pdf.rect(margin, yPosition, pageWidth - (margin * 2), metadataHeight, 'F');
    pdf.setDrawColor(colors.border[0], colors.border[1], colors.border[2]);
    pdf.setLineWidth(0.5);
    pdf.rect(margin, yPosition, pageWidth - (margin * 2), metadataHeight);

    pdf.setTextColor(colors.text[0], colors.text[1], colors.text[2]);
    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'normal');
    pdf.text(`Generated: ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}`, margin + 5, yPosition + 5);
    pdf.text(`Job Reference: ${item.jobNumber || 'Pending Assignment'}`, margin + 5, yPosition + 10);
    pdf.text(`Status: ${this.getWorkflowStatus(item)}`, pageWidth - margin - 60, yPosition + 5);
    pdf.text(`Quote No: ${item.quotationNo || 'Pending'}`, pageWidth - margin - 60, yPosition + 10);

    return yPosition + metadataHeight;
  }

  // Standardized Section Title
  // Compact Section Title
  private addSectionTitle(pdf: jsPDF, title: string, yPosition: number, colors: any, pageWidth: number, margin: number): number {
    const titleHeight = 10; // Reduced from 12 to 10

    // Section background
    pdf.setFillColor(colors.primary[0], colors.primary[1], colors.primary[2]);
    pdf.rect(margin, yPosition, pageWidth - (margin * 2), titleHeight, 'F');

    // Accent bar
    pdf.setFillColor(colors.accent[0], colors.accent[1], colors.accent[2]);
    pdf.rect(margin, yPosition, 3, titleHeight, 'F'); // Reduced from 4 to 3

    // Title text
    pdf.setTextColor(colors.white[0], colors.white[1], colors.white[2]);
    pdf.setFontSize(9); // Reduced from 10
    pdf.setFont('helvetica', 'bold');
    pdf.text(title, margin + 6, yPosition + 6); // Adjusted position

    return yPosition + titleHeight + 5; // Reduced spacing from 8 to 5
  }


  // Standardized Professional Table
  private addProfessionalTable(pdf: jsPDF, data: string[][], yPosition: number, colors: any, pageWidth: number, margin: number, hasHeader: boolean = false): number {
    const rowHeight = 8;
    const tableWidth = pageWidth - (margin * 2);

    for (let i = 0; i < data.length; i++) {
      const [label, value] = data[i];

      // Row background (alternating colors)
      const bgColor = i % 2 === 0 ? colors.tableHeader : colors.white;
      pdf.setFillColor(bgColor[0], bgColor[1], bgColor[2]);
      pdf.rect(margin, yPosition, tableWidth, rowHeight, 'F');

      // Row border
      pdf.setDrawColor(colors.border[0], colors.border[1], colors.border[2]);
      pdf.setLineWidth(0.3);
      pdf.rect(margin, yPosition, tableWidth, rowHeight);

      // Text content
      pdf.setTextColor(colors.text[0], colors.text[1], colors.text[2]);
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'bold');
      pdf.text(label + ':', margin + 4, yPosition + 5);

      pdf.setFont('helvetica', 'normal');
      const valueLines = typeof value === 'string' ? value.split('\n') : [value];
      for (let j = 0; j < valueLines.length; j++) {
        pdf.text(valueLines[j], margin + 60, yPosition + 5 + (j * 3));
      }

      yPosition += Math.max(rowHeight, valueLines.length * 3 + 2);
    }

    return yPosition;
  }

  // Standardized Financial Summary Cards
  // Compact Financial Summary Cards
  private addFinancialSummaryCards(pdf: jsPDF, financial: any, yPosition: number, colors: any, pageWidth: number, margin: number): number {
    const cardWidth = (pageWidth - (margin * 2) - 9) / 4; // Reduced spacing from 15 to 9
    const cardHeight = 18; // Reduced from 25 to 18
    const cardSpacing = 3; // Reduced from 5 to 3

    const cards = [
      { title: 'Project Value', amount: financial.totalPrice, color: colors.accent, subtitle: 'Contract' },
      { title: 'Total Invoice', amount: financial.totalInvoice, color: colors.primary, subtitle: 'Billed' },
      { title: 'Total Payment', amount: financial.totalPayment, color: financial.totalPayment > 0 ? colors.success : colors.border, subtitle: 'Received' },
      { title: 'Outstanding', amount: financial.outstanding, color: financial.outstanding > 0 ? colors.warning : colors.success, subtitle: financial.outstanding > 0 ? 'Due' : 'Complete' }
    ];

    for (let i = 0; i < cards.length; i++) {
      const card = cards[i];
      const cardX = margin + (i * (cardWidth + cardSpacing));

      this.addCompactCard(pdf, card.title, `$${card.amount.toFixed(2)}`, card.subtitle,
        cardX, yPosition, cardWidth, cardHeight, card.color, colors);
    }

    return yPosition + cardHeight;
  }

  // Standardized Card Component
  // Compact Card Component
  private addCompactCard(pdf: jsPDF, title: string, amount: string, subtitle: string,
    x: number, y: number, width: number, height: number, color: number[], colors: any): void {

    // Card background (remove shadow for compactness)
    pdf.setFillColor(colors.white[0], colors.white[1], colors.white[2]);
    pdf.rect(x, y, width, height, 'F');

    // Card border
    pdf.setDrawColor(color[0], color[1], color[2]);
    pdf.setLineWidth(0.8); // Reduced from 1
    pdf.rect(x, y, width, height);

    // Top accent bar (reduced height)
    pdf.setFillColor(color[0], color[1], color[2]);
    pdf.rect(x, y, width, 2, 'F'); // Reduced from 3 to 2

    // Compact card content
    pdf.setTextColor(colors.text[0], colors.text[1], colors.text[2]);
    pdf.setFontSize(7); // Reduced from 8
    pdf.setFont('helvetica', 'bold');
    pdf.text(title, x + 2, y + 6); // Reduced padding

    pdf.setFontSize(9); // Reduced from 11
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(color[0], color[1], color[2]);
    pdf.text(amount, x + 2, y + 12); // Adjusted position

    pdf.setFontSize(6); // Reduced from 7
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(colors.text[0], colors.text[1], colors.text[2]);
    pdf.text(`(${subtitle})`, x + 2, y + 16); // Adjusted position
  }


  // Standardized Data Table
  private addDataTable(pdf: jsPDF, headers: string[], data: string[][], yPosition: number, colors: any, pageWidth: number, margin: number): number {
    const tableWidth = pageWidth - (margin * 2);
    const colWidth = tableWidth / headers.length;
    const rowHeight = 8;

    // Table header
    pdf.setFillColor(colors.primary[0], colors.primary[1], colors.primary[2]);
    pdf.rect(margin, yPosition, tableWidth, 10, 'F');

    pdf.setTextColor(colors.white[0], colors.white[1], colors.white[2]);
    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'bold');

    for (let i = 0; i < headers.length; i++) {
      pdf.text(headers[i], margin + (i * colWidth) + 4, yPosition + 7);
    }

    yPosition += 10;

    // Table rows
    pdf.setTextColor(colors.text[0], colors.text[1], colors.text[2]);
    pdf.setFont('helvetica', 'normal');

    for (let i = 0; i < data.length; i++) {
      const row = data[i];

      // Row background
      const bgColor = i % 2 === 0 ? colors.tableHeader : colors.white;
      pdf.setFillColor(bgColor[0], bgColor[1], bgColor[2]);
      pdf.rect(margin, yPosition, tableWidth, rowHeight, 'F');

      // Row border
      pdf.setDrawColor(colors.border[0], colors.border[1], colors.border[2]);
      pdf.setLineWidth(0.3);
      pdf.rect(margin, yPosition, tableWidth, rowHeight);

      // Column borders
      for (let j = 0; j < headers.length; j++) {
        if (j > 0) {
          pdf.line(margin + (j * colWidth), yPosition, margin + (j * colWidth), yPosition + rowHeight);
        }
      }

      // Cell content
      pdf.setFontSize(7);
      for (let j = 0; j < row.length && j < headers.length; j++) {
        const cellValue = row[j];
        // Special formatting for status
        if (j === headers.length - 1 && (cellValue === 'RECEIVED' || cellValue === 'PENDING')) {
          const statusColor = cellValue === 'RECEIVED' ? colors.success : colors.warning;
          pdf.setTextColor(statusColor[0], statusColor[1], statusColor[2]);
          pdf.setFont('helvetica', 'bold');
        } else {
          pdf.setTextColor(colors.text[0], colors.text[1], colors.text[2]);
          pdf.setFont('helvetica', 'normal');
        }
        pdf.text(cellValue, margin + (j * colWidth) + 4, yPosition + 5);
      }

      yPosition += rowHeight;
    }

    return yPosition;
  }
  // Ultra-Professional Card-Style Payment Progress
  private addPaymentProgressSection(pdf: jsPDF, item: any, yPosition: number, colors: any, pageWidth: number, margin: number): number {
    const totalInvoiceAmount = this.calculateTotalInvoiceAmount(item.invoices || []);
    const totalPaymentAmount = this.calculateTotalPaymentAmount(item.payments || []);
    const totalPrice = parseFloat(item.totalPrice) || 0;
    let baseAmount = totalInvoiceAmount > 0 ? totalInvoiceAmount : totalPrice;

    if (baseAmount === 0) return yPosition;

    const paymentPercentage = Math.min((totalPaymentAmount / baseAmount) * 100, 100);
    const remainingAmount = Math.max(0, baseAmount - totalPaymentAmount);

    // **COMPACT CONTAINER WITH IMPROVED SPACING**
    const containerWidth = pageWidth - (margin * 2);
    const containerHeight = 30; // Increased from 22 for better spacing

    // Minimal shadow
    pdf.setFillColor(245, 245, 245);
    pdf.rect(margin + 0.5, yPosition + 0.5, containerWidth, containerHeight, 'F');

    // Main background
    pdf.setFillColor(255, 255, 255);
    pdf.rect(margin, yPosition, containerWidth, containerHeight, 'F');

    // Thin border
    pdf.setDrawColor(200, 200, 200);
    pdf.setLineWidth(0.2);
    pdf.rect(margin, yPosition, containerWidth, containerHeight);

    // Status color
    const statusColor = paymentPercentage >= 100 ? [34, 197, 94] :
      paymentPercentage >= 50 ? [59, 130, 246] : [239, 68, 68];

    // Thin accent line
    pdf.setFillColor(statusColor[0], statusColor[1], statusColor[2]);
    pdf.rect(margin, yPosition, containerWidth, 1.5, 'F');

    // **UNIQUE HEADER TITLE**
    pdf.setTextColor(40, 40, 40);
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'bold');
    pdf.text('PAYMENT STATUS OVERVIEW', margin + 8, yPosition + 10); // Changed title to avoid duplication

    // Mini status badge
    const statusText = paymentPercentage >= 100 ? 'PAID' : paymentPercentage >= 50 ? 'PROGRESS' : 'PENDING';
    const badgeWidth = 35;
    const badgeHeight = 8;
    const badgeX = margin + containerWidth - badgeWidth - 8; // Increased margin
    const badgeY = yPosition + 4;

    pdf.setFillColor(statusColor[0], statusColor[1], statusColor[2]);
    pdf.roundedRect(badgeX, badgeY, badgeWidth, badgeHeight, 4, 4, 'F');

    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(5);
    pdf.setFont('helvetica', 'bold');
    const statusTextWidth = pdf.getTextWidth(statusText);
    pdf.text(statusText, badgeX + (badgeWidth - statusTextWidth) / 2, badgeY + 5.5);

    // **MINI PROGRESS BAR WITH BETTER SPACING**
    const progressY = yPosition + 15; // Increased spacing
    const progressWidth = containerWidth - 16; // Increased margins
    const progressHeight = 4;
    const progressX = margin + 8; // Increased margin

    // Track
    pdf.setFillColor(248, 248, 248);
    pdf.roundedRect(progressX, progressY, progressWidth, progressHeight, 2, 2, 'F');

    // Fill
    const fillWidth = (progressWidth * paymentPercentage) / 100;
    if (fillWidth > 2) {
      pdf.setFillColor(statusColor[0], statusColor[1], statusColor[2]);
      pdf.roundedRect(progressX, progressY, fillWidth, progressHeight, 2, 2, 'F');
    }

    // Percentage text with better positioning
    pdf.setTextColor(60, 60, 60);
    pdf.setFontSize(6);
    pdf.setFont('helvetica', 'bold');
    const percentText = `${Math.round(paymentPercentage)}%`;
    const percentWidth = pdf.getTextWidth(percentText);
    pdf.text(percentText, progressX + progressWidth - percentWidth, progressY + 3);

    // **CONDENSED FINANCIAL INFO WITH IMPROVED SPACING**
    const detailsY = yPosition + 23; // Increased spacing
    const sectionWidth = (containerWidth - 24) / 3; // Adjusted for better margins

    // Total
    pdf.setTextColor(100, 100, 100);
    pdf.setFontSize(5);
    pdf.setFont('helvetica', 'normal');
    pdf.text('TOTAL', margin + 8, detailsY - 1);

    pdf.setTextColor(40, 40, 40);
    pdf.setFontSize(7);
    pdf.setFont('helvetica', 'bold');
    pdf.text(`$${baseAmount.toLocaleString('en-US', { maximumFractionDigits: 0 })}`,
      margin + 8, detailsY + 3);

    // Paid
    const paidX = margin + 8 + sectionWidth;
    pdf.setTextColor(100, 100, 100);
    pdf.setFontSize(5);
    pdf.setFont('helvetica', 'normal');
    pdf.text('PAID', paidX, detailsY - 1);

    pdf.setTextColor(34, 197, 94);
    pdf.setFontSize(7);
    pdf.setFont('helvetica', 'bold');
    pdf.text(`$${totalPaymentAmount.toLocaleString('en-US', { maximumFractionDigits: 0 })}`,
      paidX, detailsY + 3);

    // Balance
    const balanceX = margin + 8 + (sectionWidth * 2);
    pdf.setTextColor(100, 100, 100);
    pdf.setFontSize(5);
    pdf.setFont('helvetica', 'normal');
    pdf.text('BALANCE', balanceX, detailsY - 1);

    const balanceColor = remainingAmount > 0 ? [239, 68, 68] : [34, 197, 94];
    pdf.setTextColor(balanceColor[0], balanceColor[1], balanceColor[2]);
    pdf.setFontSize(7);
    pdf.setFont('helvetica', 'bold');
    pdf.text(`$${remainingAmount.toLocaleString('en-US', { maximumFractionDigits: 0 })}`,
      balanceX, detailsY + 3);

    return yPosition + containerHeight + 4; // Increased bottom spacing
  }



  // Standardized Status Summary
  private addStatusSummary(pdf: jsPDF, item: any, outstandingAmount: number, yPosition: number, colors: any, pageWidth: number, margin: number): number {
    const isCompleted = this.isPaymentCompleted(item);
    const summaryHeight = 18; // Reduced from 30 to 18

    // Compact status box
    const statusColor = isCompleted ? colors.success : colors.warning;
    pdf.setFillColor(statusColor[0] + 40, statusColor[1] + 40, statusColor[2] + 40);
    pdf.rect(margin, yPosition, pageWidth - (margin * 2), summaryHeight, 'F');
    pdf.setDrawColor(statusColor[0], statusColor[1], statusColor[2]);
    pdf.setLineWidth(1); // Reduced from 2 to 1
    pdf.rect(margin, yPosition, pageWidth - (margin * 2), summaryHeight);

    // Compact status icon and text
    pdf.setTextColor(statusColor[0], statusColor[1], statusColor[2]);
    pdf.setFontSize(10); // Reduced from 12 to 10
    pdf.setFont('helvetica', 'bold');
    const statusText = isCompleted ? 'PAYMENT COMPLETED' : 'PAYMENT PENDING';
    const icon = isCompleted ? '✓' : '⚠';
    pdf.text(icon, margin + 6, yPosition + 8); // Adjusted position
    pdf.text(statusText, margin + 16, yPosition + 8); // Adjusted position

    // Compact description text
    pdf.setFontSize(7); // Reduced from 8 to 7
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(colors.text[0], colors.text[1], colors.text[2]);
    const statusDesc = isCompleted ?
      'All payments received and processed.' : // Shortened text
      `Outstanding: $${outstandingAmount.toFixed(2)} requires attention.`; // Shortened text
    pdf.text(statusDesc, margin + 16, yPosition + 14); // Adjusted position

    return yPosition + summaryHeight;
  }


  // Space checking utility
  private async checkSpaceAndAddPage(pdf: jsPDF, yPosition: number, heightNeeded: number, margin: number, item: any, colors: any, pageWidth: number): Promise<number> {
    const pageHeight = pdf.internal.pageSize.getHeight();

    if (yPosition + heightNeeded > pageHeight - 30) {
      pdf.addPage();
      return await this.addProfessionalHeader(pdf, item, margin, colors, pageWidth, margin) + 15;
    }
    return yPosition;
  }

  // Standardized Professional Footer
  private addProfessionalFooter(pdf: jsPDF, item: any, colors: any, pageWidth: number, pageHeight: number, margin: number): void {
    const footerY = pageHeight - 18;
    const footerHeight = 18;

    // Footer background
    pdf.setFillColor(colors.tableHeader[0], colors.tableHeader[1], colors.tableHeader[2]);
    pdf.rect(0, footerY, pageWidth, footerHeight, 'F');

    // Footer border
    pdf.setDrawColor(colors.primary[0], colors.primary[1], colors.primary[2]);
    pdf.setLineWidth(1);
    pdf.line(0, footerY, pageWidth, footerY);

    // Footer content
    pdf.setTextColor(colors.text[0], colors.text[1], colors.text[2]);
    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'bold');
    pdf.text('KIKBLOX Professional Solutions', margin, footerY + 6);
    pdf.setFont('helvetica', 'normal');
    pdf.text(`Customer: ${item.customerName || 'N/A'}`, margin, footerY + 12);

    pdf.text(`Generated: ${new Date().toLocaleDateString()}`, pageWidth - margin - 40, footerY + 6);
    pdf.text('Confidential Document', pageWidth - margin - 40, footerY + 12);
  }

  // Helper methods (keep existing wrapText, loadImage, calculation methods)
  private wrapText(text: string, maxLength: number): string {
    if (!text || text.length <= maxLength) return text;

    const words = text.split(' ');
    const lines: string[] = [];
    let currentLine = '';

    for (const word of words) {
      if ((currentLine + word).length <= maxLength) {
        currentLine += (currentLine ? ' ' : '') + word;
      } else {
        if (currentLine) lines.push(currentLine);
        currentLine = word;
      }
    }

    if (currentLine) lines.push(currentLine);
    return lines.join('\n');
  }

  private async loadImage(src: string): Promise<string> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = img.width;
        canvas.height = img.height;
        ctx?.drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      };
      img.onerror = reject;
      img.src = src;
    });
  }
  // New helper methods for handling multiple invoices and payments
  formatInvoicesForDisplay(invoices: any[]): string {
    if (!invoices || invoices.length === 0) return '';
    return invoices.map(inv => `${inv.invoiceNo} (${inv.invoicePrice})`).join(', ');
  }
  // Add this method to check if payment is completed
  isPaymentCompleted(item: any): boolean {
    const totalInvoiced = this.calculateTotalInvoiceAmount(item.invoices || []);
    const totalPaid = this.calculateTotalPaymentAmount(item.payments || []);
    return totalInvoiced > 0 && totalInvoiced <= totalPaid;
  }

  formatPaymentsForDisplay(payments: any[]): string {
    if (!payments || payments.length === 0) return '';
    return payments
      .map(pay => {
        const invoice = pay.invoiceNo && pay.invoiceNo.trim() !== '' ? pay.invoiceNo : 'Update Invoice No';
        return `${invoice}: ${pay.paymentPrice}`;
      })
      .join(', ');
  }

  calculateTotalInvoiceAmount(invoices: any[]): number {
    if (!invoices || invoices.length === 0) return 0;
    return invoices.reduce((total, inv) => total + (inv.invoicePrice || 0), 0);
  }

  calculateTotalPaymentAmount(payments: any[]): number {
    if (!payments || payments.length === 0) return 0;
    return payments.reduce((total, pay) => total + (pay.paymentPrice || 0), 0);
  }

  calculateOutstandingAmount(invoices: any[], payments: any[]): number {
    const totalInvoice = this.calculateTotalInvoiceAmount(invoices);
    const totalPayment = this.calculateTotalPaymentAmount(payments);
    return totalInvoice - totalPayment;
  }

  // Updated method to get invoice numbers for filtering
  getInvoiceNumbers(item: any): string {
    if (item.invoices && item.invoices.length > 0) {
      return item.invoices.map((inv: any) => inv.invoiceNo).join(', ');
    }
    // Fallback for legacy data
    return item.invoiceNo || '';
  }

  // Updated method to check if item has invoices
  hasInvoices(item: any): boolean {
    return (item.invoices && item.invoices.length > 0) || !!item.invoiceNo;
  }

  // Updated method to check if item has payments
  hasPayments(item: any): boolean {
    return (item.payments && item.payments.length > 0) || !!item.paymentReceivedDate;
  }

  resetTabLoadingStates(): void {
    Object.keys(this.tabLoadingStates).forEach(key => {
      this.tabLoadingStates[key as keyof typeof this.tabLoadingStates] = false;
    });
  }

  private processPaymentDate(dateString: string): string {
    if (!dateString) return '';

    // If it contains comma, it's multiple dates
    if (dateString.includes(',')) {
      const dates = dateString.split(',').map(date => date.trim());

      // Process each date
      const processedDates = dates.map((date, index) => {
        if (index > 0 && date.split('/').length === 2) {
          const firstDate = new Date(dates[0]);
          const year = firstDate.getFullYear();
          return `${year}/${date}`;
        }
        return date;
      });

      return processedDates.join(', ');
    }

    return dateString;
  }

  toggleEmailVisibility(item: any): void {
    item.showEmail = !item.showEmail;
  }

  toggleProjectNameVisibility(item: any): void {
    item.showFullProjectName = !item.showFullProjectName;
  }

  toggleCustomerNameVisibility(item: any): void {
    item.showFullCustomerName = !item.showFullCustomerName;
  }

  toggleRemarksVisibility(item: any): void {
    item.showFullRemarks = !item.showFullRemarks;
  }

  loadWorkflowItems(): void {
    this.loading = true;
    this.workflowItems = [];
  }

  applyDateRangeFilter(): void {
    // ✅ Guard: ensure BOTH dates are valid Date objects
    if (
      !this.dateRange ||
      this.dateRange.length < 2 ||
      !(this.dateRange[0] instanceof Date) ||
      !(this.dateRange[1] instanceof Date) ||
      isNaN(this.dateRange[0].getTime()) ||
      isNaN(this.dateRange[1].getTime())
    ) {
      return; // Don't filter yet — user hasn't finished selecting range
    }

    const startDate = new Date(this.dateRange[0]);
    startDate.setHours(0, 0, 0, 0);

    const endDate = new Date(this.dateRange[1]);
    endDate.setHours(23, 59, 59, 999);

    this.filteredWorkflowItems = this.workflowItems.filter(item =>
      this.isItemInDateRange(item, startDate, endDate)
    );
  }

  private isItemInDateRange(item: any, startDate: Date, endDate: Date): boolean {
    switch (this.selectedDateFilterType) {

      case 'quotationDate': {
        const d = this.parseDateSafe(item.quotationDate);
        return d !== null && d >= startDate && d <= endDate;
      }

      case 'paymentDate': {
        // Check across all payments — match if ANY payment falls in range
        if (!item.payments || item.payments.length === 0) return false;
        return item.payments.some((payment: any) => {
          const d = this.parseDateSafe(payment.paymentReceivedDate);
          return d !== null && d >= startDate && d <= endDate;
        });
      }

      case 'invoiceDate': {
        // Check across all invoices — match if ANY invoice date falls in range
        if (!item.invoices || item.invoices.length === 0) return false;
        return item.invoices.some((invoice: any) => {
          const d = this.parseDateSafe(invoice.date);
          return d !== null && d >= startDate && d <= endDate;
        });
      }

      default:
        return true;
    }
  }

  /**
   * Safely parses a date string/value.
   * Returns null if invalid or empty — avoids NaN edge cases.
   */
  private parseDateSafe(value: any): Date | null {
    if (!value || value === '' || value === null || value === undefined) {
      return null;
    }
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }

  clearDateRangeFilter(): void {
    this.dateRange = [];
    this.filteredWorkflowItems = [...this.workflowItems];
  }
  onDateFilterTypeChange(): void {
    // Reset filter when switching filter type
    this.dateRange = [];
    this.filteredWorkflowItems = [...this.workflowItems];
  }
  onHeaderCollapse(isCollapsed: boolean) {
    this.isHeaderCollapsed = isCollapsed;
    console.log('Header collapsed:', isCollapsed);
    console.log('Header state changed:', this.isHeaderCollapsed);
  }

  // Convert Excel serial date to YYYY-MM-DD string
  private convertExcelDate(serial: number | string | Date): string {
    if (!serial) return '';

    // Handle comma-separated dates
    if (typeof serial === 'string' && serial.includes(',')) {
      const dates = serial.split(',').map(d => d.trim());
      // Process each date and return the first valid one
      for (const dateStr of dates) {
        const parsedDate = this.parseSingleDate(dateStr);
        if (parsedDate) return parsedDate;
      }
      console.warn(`No valid date found in: ${serial}`);
      return '';
    }

    return this.parseSingleDate(serial) || '';
  }

  private parseSingleDate(serial: number | string | Date): string | null {
    if (!serial) return null;

    // Handle Date object
    if (serial instanceof Date && !isNaN(serial.getTime())) {
      return `${serial.getUTCFullYear()}-${String(serial.getUTCMonth() + 1).padStart(2, '0')}-${String(
        serial.getUTCDate()
      ).padStart(2, '0')}`;
    }

    // Handle Excel serial number
    if (typeof serial === 'number') {
      const excelEpoch = new Date(Date.UTC(1899, 11, 30));
      const days = Math.floor(serial);
      const milliseconds = Math.round((serial - days) * 24 * 60 * 60 * 1000);
      const date = new Date(excelEpoch.getTime() + days * 24 * 60 * 60 * 1000 + milliseconds);
      if (!isNaN(date.getTime())) {
        return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(
          date.getUTCDate()
        ).padStart(2, '0')}`;
      }
      return null;
    }

    // Handle string dates
    if (typeof serial === 'string') {
      let date: Date | null = null;

      // Try parsing common formats
      const formats = [
        { pattern: /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/, handler: (m: RegExpMatchArray) => new Date(`${m[3]}-${m[1]}-${m[2]}`) }, // MM/DD/YYYY
        { pattern: /^(\d{4})\/(\d{1,2})\/(\d{1,2})$/, handler: (m: RegExpMatchArray) => new Date(`${m[1]}-${m[2]}-${m[3]}`) }, // YYYY/MM/DD
        { pattern: /^(\d{1,2})\/(\d{1,2})$/, handler: (m: RegExpMatchArray) => new Date(`${new Date().getFullYear()}-${m[1]}-${m[2]}`) }, // MM/DD (assume current year)
      ];

      for (const { pattern, handler } of formats) {
        const match = serial.match(pattern);
        if (match) {
          date = handler(match);
          break;
        }
      }

      // Try parsing with Date directly
      if (!date) {
        date = new Date(serial);
      }

      if (date && !isNaN(date.getTime())) {
        return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(
          date.getUTCDate()
        ).padStart(2, '0')}`;
      }
    }

    console.warn(`Invalid date format: ${serial}`);
    return null;
  }

clearTables(): void {
  this.clearConfirmPassword = '';
  this.clearPasswordError = '';
  this.clearSuccess = false;
  this.clearError = '';
  this.showClearConfirmDialog = true;
}
confirmClearTables(): void {
  this.clearPasswordError = '';
  this.clearError = '';

  if (!this.clearConfirmPassword || this.clearConfirmPassword.trim() === '') {
    this.clearPasswordError = 'Password is required.';
    return;
  }

  // Verify password against current user credentials
  const currentUser = this.authService.getCurrentUser();
  this.isClearing = true;

  this.authService.verifyPassword(currentUser.email, this.clearConfirmPassword).subscribe({
    next: (isValid: boolean) => {
      if (!isValid) {
        this.clearPasswordError = 'Incorrect password. Please try again.';
        this.isClearing = false;
        return;
      }

      // Password verified — proceed with clearing
      this.executeClearTables();
    },
    error: (err) => {
      this.clearPasswordError = 'Password verification failed. Please try again.';
      this.isClearing = false;
      console.error('Password verification error:', err);
    }
  });
}

// Step 3: Execute the actual clear
private executeClearTables(): void {
  this.workflowDataService.clearTables().subscribe({
    next: (response) => {
      console.log(response.message);
      this.clearSuccess = true;
      this.isClearing = false;
      this.workflowDataService.loadWorkflowItems();

      // Auto-close dialog after short delay
      setTimeout(() => {
        this.closeClearConfirmDialog();
      }, 1500);
    },
    error: (err) => {
      console.error('Failed to clear tables:', err);
      this.clearError = 'Failed to clear tables. Please try again or contact support.';
      this.isClearing = false;
    }
  });
}

closeClearConfirmDialog(): void {
  if (this.isClearing) return; // Block close while clearing
  this.showClearConfirmDialog = false;
  this.clearConfirmPassword = '';
  this.clearPasswordError = '';
  this.clearSuccess = false;
  this.clearError = '';
}
  // Enhanced delete selected workflows with better confirmation
  deleteSelectedWorkflows(): void {
    if (this.selectedWorkflowItems.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'No Selection',
        detail: 'Please select at least one document to delete',
        life: 3000
      });
      return;
    }

    const itemCount = this.selectedWorkflowItems.length;
    const itemText = itemCount === 1 ? 'document' : 'documents';

    this.confirmationService.confirm({
      message: `
        <div style="text-align: center;">
          <i class="pi pi-exclamation-triangle" style="font-size: 3rem; color: var(--red-500);"></i>
          <h3>Delete Confirmation</h3>
          <p>Are you sure you want to delete <strong>${itemCount}</strong> selected ${itemText}?</p>
          <p style="color: var(--red-600); font-size: 0.9rem;">
            <i class="pi pi-info-circle"></i> This action cannot be undone.
          </p>
        </div>
      `,
      header: 'Confirm Deletion',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-secondary',
      acceptLabel: 'Yes, Delete',
      rejectLabel: 'Cancel',
      accept: () => {
        // Show progress for deletion
        this.messageService.add({
          severity: 'info',
          summary: 'Deleting...',
          detail: `Deleting ${itemCount} ${itemText}...`,
          life: 2000
        });

        const deleteObservables = this.selectedWorkflowItems.map(item =>
          this.workflowDataService.deleteWorkflow(item.orderId)
        );

        // Use forkJoin to handle multiple deletions
        forkJoin(deleteObservables).subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success',
              summary: 'Deleted Successfully',
              detail: `${itemCount} ${itemText} deleted successfully`,
              life: 3000
            });
            this.selectedWorkflowItems = []; // Clear selection
            this.workflowDataService.loadWorkflowItems(); // Refresh list
          },
          error: (err) => {
            this.messageService.add({
              severity: 'error',
              summary: 'Deletion Failed',
              detail: `Failed to delete some ${itemText}. Please try again.`,
              life: 5000
            });
            console.error('Delete error:', err);
          }
        });
      },
      reject: () => {
        // Optional: Show message when user cancels
        this.messageService.add({
          severity: 'info',
          summary: 'Cancelled',
          detail: 'Delete operation cancelled',
          life: 2000
        });
      }
    });
  }

  // Enhanced single item delete
  deleteWorkflow(item: any): void {
    this.confirmationService.confirm({
      message: `
        <div style="text-align: center;">
          <i class="pi pi-exclamation-triangle" style="font-size: 3rem; color: var(--red-500);"></i>
          <h3>Delete Confirmation</h3>
          <p>Are you sure you want to delete this document?</p>
          <div style="background: var(--surface-100); padding: 1rem; border-radius: 6px; margin: 1rem 0;">
            <strong>Customer:</strong> ${item.customerName || 'N/A'}<br>
            <strong>Project:</strong> ${item.project || 'N/A'}<br>
            <strong>Quotation No:</strong> ${item.quotationNo || 'N/A'}
          </div>
          <p style="color: var(--red-600); font-size: 0.9rem;">
            <i class="pi pi-info-circle"></i> This action cannot be undone.
          </p>
        </div>
      `,
      header: 'Confirm Deletion',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-secondary',
      acceptLabel: 'Yes, Delete',
      rejectLabel: 'Cancel',
      accept: () => {
        this.workflowDataService.deleteWorkflow(item.orderId).subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success',
              summary: 'Deleted Successfully',
              detail: 'Document deleted successfully',
              life: 3000
            });
            this.workflowDataService.loadWorkflowItems();
          },
          error: (err) => {
            this.messageService.add({
              severity: 'error',
              summary: 'Deletion Failed',
              detail: 'Failed to delete document. Please try again.',
              life: 3000
            });
            console.error('Delete error:', err);
          }
        });
      }
    });
  }

  // Method to close upload dialog manually
  closeUploadDialog(): void {
    if (!this.isUploading) {
      this.showUploadDialog = false;
    }
  }

  // Enhanced file upload method with progress dialog
  onFileUpload(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];

      // Reset states and show upload dialog
      this.showUploadDialog = true;
      this.uploadProgress = 0;
      this.uploadStatus = 'Reading file...';
      this.isUploading = true;
      this.uploadedFileName = file.name;
      this.uploadError = ''; // Reset error

      const reader = new FileReader();

      reader.onprogress = (e) => {
        if (e.lengthComputable) {
          this.uploadProgress = Math.round((e.loaded / e.total) * 30); // 30% for file reading
          this.uploadStatus = `Reading file... ${this.uploadProgress}%`;
          console.log('uploadProgress', this.uploadProgress)
        }
      };

      reader.onload = (e: any) => {
        this.uploadProgress = 30;
        this.uploadStatus = 'Processing Excel data...';

        // Add a small delay to show progress visually
        setTimeout(() => {
          try {
            const data = new Uint8Array(e.target.result);
            const workbook = XLSX.read(data, {
              type: 'array',
              cellDates: true,
              dateNF: 'yyyy-mm-dd'
            });

            this.uploadProgress = 50;
            this.uploadStatus = 'Mapping data...';

            const firstSheet = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[firstSheet];

            const jsonData = XLSX.utils.sheet_to_json(worksheet, {
              defval: '',
              raw: false
            });

            console.log('Raw JSON data:', jsonData);

            const mappedData = this.mapExcelData(jsonData);

            if (mappedData.length === 0) {
              this.uploadError = 'No valid rows found in the Excel file';
              this.isUploading = false;
              this.messageService.add({
                severity: 'warn',
                summary: 'No Valid Data',
                detail: 'No valid rows found in the Excel file',
                life: 3000
              });
              // Clear the input value to allow re-upload of same file
              input.value = '';
              return;
            }

            this.uploadProgress = 60;
            this.uploadStatus = `Processing ${mappedData.length} records...`;
            console.log('Mapped data:', mappedData);

            // Check for conflicts sequentially
            this.checkForConflicts(mappedData);

          } catch (error) {
            this.uploadError = 'Failed to process Excel file';
            this.isUploading = false;
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Failed to process Excel file',
              life: 3000
            });
            console.error('Excel processing error:', error);
            // Clear the input value to allow re-upload of same file
            input.value = '';
          }
        }, 500); // Small delay to show progress
      };

      reader.onerror = () => {
        this.uploadError = 'Failed to read the Excel file';
        this.isUploading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to read the Excel file',
          life: 3000
        });
        // Clear the input value to allow re-upload of same file
        input.value = '';
      };

      reader.readAsArrayBuffer(file);
    }

    // Always clear the input value at the end to allow re-upload of same file
    setTimeout(() => {
      input.value = '';
    }, 100);
  }


  async checkForConflicts(mappedData: any[]): Promise<void> {
    this.uploadStatus = 'Checking for existing data...';
    this.uploadProgress = 60;
    this.conflictItems = [];
    const nonConflicts: any[] = [];

    // Validate mapped data before processing
    const validatedData = mappedData.filter(workflow => {
      const isValid = workflow &&
        typeof workflow === 'object' &&
        workflow.quotationNo &&
        workflow.quotationNo.trim() !== '';

      if (!isValid) {
        console.warn('Invalid workflow detected:', workflow);
      }

      return isValid;
    });

    console.log(`Filtered ${mappedData.length - validatedData.length} invalid workflows`);

    if (validatedData.length === 0) {
      this.uploadError = 'No valid workflows found after processing';
      this.isUploading = false;
      this.messageService.add({
        severity: 'warn',
        summary: 'No Valid Data',
        detail: 'No valid workflows found after processing',
        life: 3000
      });
      return;
    }

    // Process each workflow one by one
    for (let i = 0; i < validatedData.length; i++) {
      const workflow = validatedData[i];
      this.uploadStatus = `Checking record ${i + 1} of ${validatedData.length}...`;
      this.uploadProgress = 60 + ((i / validatedData.length) * 20); // Progress from 60% to 80%

      // Enhanced logging for debugging
      console.log(`Checking workflow ${i + 1}:`, {
        quotationNo: workflow.quotationNo,
        hasData: !!workflow,
        keys: Object.keys(workflow || {}),
        dataSize: JSON.stringify(workflow).length
      });

      try {
        // Validate workflow before sending
        if (!workflow || !workflow.quotationNo) {
          console.warn(`Skipping invalid workflow at index ${i}:`, workflow);
          continue;
        }

        // Create a clean copy of workflow to avoid reference issues
        const cleanWorkflow = JSON.parse(JSON.stringify(workflow));

        const conflictResult = await this.workflowDataService.checkConflicts(cleanWorkflow).toPromise();

        if (conflictResult && conflictResult.isConflict && conflictResult.conflict) {
          this.conflictItems.push({
            ...conflictResult.conflict,
            selected: true // Default to selected
          });
          console.log(`Conflict found for record ${i + 1}:`, conflictResult.conflict);
        } else {
          nonConflicts.push(workflow);
          console.log(`No conflict for record ${i + 1}`);
        }
      } catch (error: any) {  // Type the error as 'any' or create an interface
        const err = error as any; // Cast to any to access properties

        console.error(`Conflict check error for record ${i + 1}:`, {
          error: err,
          workflowData: workflow,
          url: err?.url,
          status: err?.status,
          statusText: err?.statusText,
          errorMessage: err?.message
        });

        // Handle different types of errors
        if (err?.status === 400) {
          console.warn(`Skipping record ${i + 1} due to validation error: ${err?.error?.message || err?.message}`);
          // Treat validation errors as non-conflicts and continue
          nonConflicts.push(workflow);
          continue;
        } else if (err?.status === 404) {
          console.warn(`API endpoint not found for record ${i + 1}, treating as non-conflict`);
          nonConflicts.push(workflow);
          continue;
        } else if (err?.status >= 500) {
          // Server errors - stop processing
          this.showUploadDialog = false;
          this.isUploading = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Server Error',
            detail: `Server error while checking conflicts for record ${i + 1}`,
            life: 3000
          });
          return;
        } else if (err?.status === 0 || !err?.status) {
          // Network errors
          this.showUploadDialog = false;
          this.isUploading = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Network Error',
            detail: `Network error while checking conflicts. Please check your connection.`,
            life: 3000
          });
          return;
        } else {
          // Other client errors - continue processing
          console.warn(`Client error for record ${i + 1}, continuing with next record`);
          nonConflicts.push(workflow);
          continue;
        }
      }

      // Small delay to prevent overwhelming the server
      if (i < validatedData.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }

    this.uploadProgress = 80;

    console.log(`Conflict check completed. Conflicts: ${this.conflictItems.length}, Non-conflicts: ${nonConflicts.length}`);

    if (this.conflictItems.length > 0) {
      // Show conflict dialog if conflicts were found
      this.showUploadDialog = false;
      this.isUploading = false;
      this.showConflictDialog = true;
      console.log('conflictItems', this.conflictItems);

      // Process non-conflict items immediately
      if (nonConflicts.length > 0) {
        this.processNonConflictItems(nonConflicts);
      }
    } else {
      // No conflicts, proceed normally
      this.uploadStatus = 'Uploading to database...';
      this.processAllItems(validatedData);
    }
  }

  // Process items without conflicts
  processNonConflictItems(nonConflictItems: any[]): void {
    if (nonConflictItems.length > 0) {
      this.workflowDataService.batchInsertWorkflows(nonConflictItems).subscribe({
        next: (results) => {
          this.messageService.add({
            severity: 'success',
            summary: 'Partial Success',
            detail: `${nonConflictItems.length} new records added successfully`,
            life: 3000
          });
        },
        error: (err) => {
          console.error('Non-conflict items insert error:', err);
        }
      });
    }
  }

  // Process all items (no conflicts scenario)
  processAllItems(mappedData: any[]): void {
    this.workflowDataService.batchInsertWorkflows(mappedData).subscribe({
      next: (results) => {
        this.uploadProgress = 100;
        this.uploadStatus = 'Upload completed successfully!';

        setTimeout(() => {
          this.showUploadDialog = false;
          this.isUploading = false;
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: `${results.length} records processed successfully`,
            life: 3000
          });
          this.workflowDataService.loadWorkflowItems();
        }, 1500);
      },
      error: (err) => {
        this.showUploadDialog = false;
        this.isUploading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to process workflows',
          life: 3000
        });
        console.error('Batch insert error:', err);
      }
    });
  }

  resolveConflicts(): void {
    const selectedItems = this.conflictItems.filter(item => item.selected);

    if (selectedItems.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'No Items Selected',
        detail: 'Please select at least one item to update',
        life: 3000
      });
      return;
    }

    this.isProcessingConflicts = true;

    const itemsToUpdate = selectedItems.map(item => item.newData);

    this.workflowDataService.batchInsertWorkflows(itemsToUpdate).subscribe({
      next: (results) => {
        this.isProcessingConflicts = false;
        this.showConflictDialog = false;
        this.conflictItems = []; // Reset conflict items
        this.selectAllConflicts = false; // Reset select all
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: `${selectedItems.length} records updated successfully`,
          life: 3000
        });
        this.workflowDataService.loadWorkflowItems();
      },
      error: (err) => {
        this.isProcessingConflicts = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to update selected records',
          life: 3000
        });
        console.error('Conflict resolution error:', err);
      }
    });
  }

  // Cancel conflict resolution
  cancelConflictResolution(): void {
    this.showConflictDialog = false;
    this.conflictItems = [];
    this.selectAllConflicts = false;
  }


  // Update the toggleAllSelections method
  toggleAllSelections(selectAll: boolean): void {
    this.selectAllConflicts = selectAll;
    this.conflictItems.forEach(item => {
      item.selected = selectAll;
    });
  }


  // Get field display name
  getFieldDisplayName(fieldName: string): string {
    const fieldMap: { [key: string]: string } = {
      quotationNo: 'Quotation No',
      quotationDate: 'Quotation Date',
      customerName: 'Customer Name',
      customerAddress: 'Customer Address',
      totalPrice: 'Total Price',
      poNumber: 'PO Number',
      poDate: 'PO Date',
      project: 'Project',
      country: 'Country',
      email: 'Email',
      invoices: 'Invoices',
      payments: 'Payments'
    };
    return fieldMap[fieldName] || fieldName;
  }
  mapExcelData(data: any[]): any[] {
    const normalizedColumnMap: { [key: string]: string } = {
      'inquiry received date': 'quotationDate',
      'quotation no': 'quotationNo',
      'project name': 'project',
      'customer': 'customerName',
      'enclosure type': 'enclosureType',
      'purchase order (po) no': 'poNumber',
      'po received date': 'poDate',
      'proforma invoice (pi) no': 'proformaInvoice',
      'value usd': 'totalPrice',
      'job no.': 'jobNumber',
      'invoice no': 'invoiceNo',
      'bl date': 'blDate',
      'payment due date': 'paymentDueDate',
      'number of delayed days': 'delayedDays',
      'invoice date': 'invoiceDate',
      'invoice value': 'invoicePrice',
      'payment date': 'paymentReceivedDate',
      'payment values': 'paymentPrice',
      'contact': 'contact',
      'email': 'email',
      'pi sent date': 'piDate',
      'method of inquiry received': 'methodOfInquiryReceived',
      'country': 'country',
      'address': 'customerAddress',
      'responsible person (engineer)': 'engineer',
      'responsible person (technical officer)': 'technicalOfficer',
    };

    // Group data by quotation number
    const groupedData: { [key: string]: any[] } = {};

    data.forEach((row, index) => {
      const mappedRow: any = { ...this.getDefaultWorkflowData() };
      let hasCustomer = false;
      let validColumnsCount = 0;

      Object.keys(row).forEach(excelKey => {
        const normalizedKey = excelKey.toLowerCase().replace(/[^a-z0-9]/g, '');
        const targetKey = Object.keys(normalizedColumnMap).find(
          mapKey => mapKey.toLowerCase().replace(/[^a-z0-9]/g, '') === normalizedKey
        );

        if (targetKey) {
          const mappedField = normalizedColumnMap[targetKey];
          let value = row[excelKey] || '';

          if (value && value.toString().trim() !== '') {
            validColumnsCount++;
          }

          if (mappedField === 'customerName' && value) {
            hasCustomer = true;
          }

          // Handle date fields
          if (['quotationDate', 'poDate', 'paymentReceivedDate', 'invoiceDate'].includes(mappedField)) {
            value = this.convertExcelDate(value);
          }

          // Handle numeric fields
          if (['totalPrice', 'paymentPrice', 'invoicePrice'].includes(mappedField)) {
            const cleanValue = value.toString().replace(/[,\s]/g, '').trim();
            value = isNaN(Number(cleanValue)) || cleanValue === '' ? 0 : Number(cleanValue);
          }

          mappedRow[mappedField] = value;
        }
      });

      if (validColumnsCount < 4) {
        console.log(`Skipping row ${index + 1}: Only ${validColumnsCount} valid columns found`);
        return;
      }

      if (mappedRow.quotationNo) {
        const normalizedQuotationNo = mappedRow.quotationNo.toLowerCase().replace(/\s+/g, '');
        if (!groupedData[normalizedQuotationNo]) {
          groupedData[normalizedQuotationNo] = [];
        }
        groupedData[normalizedQuotationNo].push(mappedRow);
      }
    });

    // Process grouped data
    const processedWorkflows: any[] = [];

    Object.keys(groupedData).forEach(normalizedQuotationNo => {
      const rows = groupedData[normalizedQuotationNo];
      const baseRow = rows[0];

      // **NEW**: Track individual totalPrices array + summed totalPrice
      const totalPrices: number[] = []; // Individual prices per row
      const totalPrice = rows.reduce((sum: number, row: any) => {
        const price = Number(row.totalPrice) || 0;
        if (price > 0) {
          totalPrices.push(price); // Store each individual price
        }
        return sum + price;
      }, 0);

      // Collect invoices and payments (existing logic)
      const invoicesMap: { [key: string]: any } = {};
      const paymentsArray: any[] = [];

      rows.forEach(row => {
        // Add invoice if it exists
        if (row.invoiceNo && (row.invoicePrice > 0 || row.invoiceNo.trim() !== '')) {
          invoicesMap[row.invoiceNo] = {
            invoiceNo: row.invoiceNo,
            date: row.invoiceDate || new Date().toISOString().split('T')[0],
            invoicePrice: Number(row.invoicePrice) || 0,
            paymentDueDate: row.paymentDueDate || null,
            blDate: row.blDate || null,
            delayedDays: Number(row.delayedDays) || 0
          };
        }

        // Add payment if it exists
        if (row.paymentReceivedDate && (row.paymentPrice > 0 || row.paymentReceivedDate.trim() !== '')) {
          paymentsArray.push({
            invoiceNo: row.invoiceNo || '',
            invoiceDate: row.invoiceDate || '',
            paymentPrice: Number(row.paymentPrice) || 0,
            paymentReceivedDate: row.paymentReceivedDate,
            paymentReceived: !!row.paymentReceivedDate
          });
        }
      });

      // Create final workflow object
      const workflow = {
        ...baseRow,
        orderId: baseRow.orderId || uuidv4(),
        quotationId: baseRow.quotationId || uuidv4(),
        poId: baseRow.poId || uuidv4(),
        piId: baseRow.piId || uuidv4(),
        workOrderId: baseRow.workOrderId || uuidv4(),
        ciId: baseRow.ciId || uuidv4(),
        paymentId: baseRow.paymentId || uuidv4(),
        invoices: Object.values(invoicesMap),
        payments: paymentsArray,
        totalPrice: totalPrice,           // Summed total (keep existing)
        totalPrices: totalPrices,         // **NEW**: Array of individual prices
        totalItems: totalPrices.length,   // **NEW**: Count of price items
        paymentReceived: paymentsArray.length > 0
      };

      console.log(`Quotation ${workflow.quotationNo}: totalPrice=${totalPrice}, totalPrices=${JSON.stringify(totalPrices)}, items=${totalPrices.length}`);

      // Set currentStep based on workflow status
      const status = this.getWorkflowStatus(workflow);
      if (status !== 'New') {
        workflow.currentStep = {
          'Quotation': 'quotation',
          'Purchase Order': 'po',
          'Proforma Invoice': 'pi',
          'Work Order': 'workOrder',
          'Commercial Invoice': 'ci',
          'Payment': 'payment',
        }[status] || 'quotation';
      }

      if (status !== 'New') {
        processedWorkflows.push(workflow);
      }
    });

    return processedWorkflows;
  }


  clearTable(): void {
    this.workflowItems = [];
    this.uploadedFileName = null;
    this.workflowDataService.updateWorkflowItems([]);
    this.messageService.add({
      severity: 'info',
      summary: 'Table Cleared',
      detail: 'Workflow data has been cleared'
    });
  }

  onGlobalFilter(event: Event): void {
    const inputValue = (event.target as HTMLInputElement).value;
    this.dt.filterGlobal(inputValue, 'contains');
  }

  openNewDialog(): void {
    this.ref = this.dialogService.open(QuotationFormDialogComponent, {
      header: '',
      width: '70%',
      styleClass: 'custom-quotation-dialog', // Unique class for this dialog
      baseZIndex: 10000,
      data: {
        startStep: 'quotation'
      }
    });

    this.ref.onClose.subscribe((result: any) => {
      console.log('result', result)
      if (result) {
        this.workflowDataService.updateWorkflowItem(result).subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: result.completed
                ? 'New quotation workflow completed'
                : `Quotation workflow saved at ${result.currentStep.toUpperCase()} step`
            });
            this.workflowDataService.loadWorkflowItems(); // Refresh list
          },
          error: (err) => {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Failed to save workflow'
            });
          }
        });
      }
    });
  }

  editWorkflow(item: any, startStep?: string): void {
    // Construct dialog data
    const dialogData: any = {
      workflowData: item,
      orderId: item.orderId,
      startStep: startStep || item.currentStep || 'quotation'
    };
    console.log('dialogData', dialogData)
    console.log('item', item)
    // Open the dialog
    this.ref = this.dialogService.open(QuotationFormDialogComponent, {
      header: 'Edit Document',
      width: '80%',
      contentStyle: { "max-height": "90vh", "overflow": "auto", "background-color": "#DBEFFF" },
      baseZIndex: 10000,
      data: dialogData
    });

    // Handle dialog close
    this.ref.onClose.subscribe((result: any) => {
      if (result) {
        this.workflowDataService.updateWorkflowItem(result).subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: result.completed
                ? 'Document workflow completed'
                : `Document workflow saved at ${result.currentStep.toUpperCase()} step`
            });
            this.workflowDataService.loadWorkflowItems(); // Refresh list
          },
          error: (err) => {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Failed to save workflow'
            });
          }
        });
      }
    });
  }

  // Updated filtering methods to handle multiple invoices and payments
  getFilteredItems(type: string): any[] {
    switch (type) {
      case 'quotation':
        return this.filteredWorkflowItems.filter(item => item.quotationNo);
      case 'po':
        return this.filteredWorkflowItems.filter(item => item.poNumber);
      case 'pi':
        return this.filteredWorkflowItems.filter(item => item.proformaInvoice);
      case 'workOrder':
        return this.filteredWorkflowItems.filter(item => item.jobNumber);
      case 'ci':
        return this.filteredWorkflowItems.filter(item => this.hasInvoices(item));
      case 'payment':
        return this.filteredWorkflowItems.filter(item => this.hasPayments(item));
      default:
        return [];
    }
  }

  // Updated getWorkflowStatus to handle multiple invoices and payments
  getWorkflowStatus(item: any): string {
    if (item.currentStep) {
      switch (item.currentStep) {
        case 'quotation': return 'Quotation';
        case 'po': return 'Purchase Order';
        case 'pi': return 'Proforma Invoice';
        case 'workOrder': return 'Work Order';
        case 'ci': return 'Commercial Invoice';
        case 'payment': return 'Payment';
      }
    }
    if (this.hasPayments(item)) {
      return 'Payment';
    } else if (this.hasInvoices(item)) {
      return 'Commercial Invoice';
    } else if (item.jobNumber) {
      return 'Work Order';
    } else if (item.proformaInvoice) {
      return 'Proforma Invoice';
    } else if (item.poNumber) {
      return 'Purchase Order';
    } else if (item.quotationNo) {
      return 'Quotation';
    }
    return 'New';
  }

  getStatusSeverity(item: any): string {
    if (item.currentStep) {
      switch (item.currentStep) {
        case 'quotation': return 'info';
        case 'po': return 'warning';
        case 'pi': return 'warning';
        case 'workOrder': return 'info';
        case 'ci': return 'success';
        case 'payment':
          // Check if payment is completed based on amounts
          return item.isPaymentCompleted ? 'success' : 'danger';
      }
    }

    if (this.hasPayments(item) || item.paymentId) {
      // Use the new completion logic instead of just paymentReceived
      return item.isPaymentCompleted ? 'success' : 'danger';
    } else if (this.hasInvoices(item)) {
      return 'success';
    } else if (item.jobNumber) {
      return 'info';
    } else if (item.proformaInvoice) {
      return 'warning';
    } else if (item.poNumber) {
      return 'warning';
    } else if (item.quotationNo) {
      return 'info';
    }
    return 'danger';
  }

  // Updated getDefaultWorkflowData to include arrays
  getDefaultWorkflowData(): any {
    return {
      quotationId: '',
      quotationNo: '',
      blDate: '',
      paymentDueDate: '',
      delayedDays: 0,
      customerName: '',
      customerAddress: '',
      project: '',
      attention: '',
      quotationDate: '',
      description: '',
      totalPrice: 0,
      poId: '',
      deliveryToCompany: '',
      deliveryToAddress: '',
      poNumber: '',
      poDate: '',
      shipDate: '',
      piId: '',
      proformaInvoice: '',
      piDate: '',
      piDescription: '',
      status: '',
      statusId: '',
      termsOfShipment: '',
      paymentTerms: '',
      workOrderId: '',
      jobNumber: '',
      poReceivedDate: '',
      deliveryTerms: '',
      officiallySubmittedDate: '',
      reviseNumber: '',
      officialSubmitDate: '',
      ciId: '',
      paymentId: '',
      uploadDoc: '',
      email: '',
      methodOfInquiryReceived: '',
      country: '',
      remarks: '',
      invoices: [],
      payments: [],
      invoiceNo: '',
      invoicePrice: 0,
      paymentPrice: 0,
      paymentReceived: false,
      paymentReceivedDate: '',
      paymentDate: '',
      ciDate: ''
    };
  }
  getSelectedCount(): number {
    return this.conflictItems.filter(item => item.selected).length;
  }
  isArrayField(fieldName: string): boolean {
    return ['invoices', 'payments'].includes(fieldName);
  }
  getArrayValue(value: any): any[] {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    return [];
  }
  formatArrayItem(fieldType: string, item: any): string {
    if (!item) return 'Empty';

    switch (fieldType) {
      case 'invoices':
        return `Invoice: ${item.invoiceNo || 'N/A'} - $${item.invoicePrice || 0} (${item.date || 'No date'})`;

      case 'payments':
        return `Payment: $${item.paymentPrice || 0} on ${item.paymentReceivedDate || 'No date'} (Invoice: ${item.invoiceNo || 'N/A'})`;

      default:
        return JSON.stringify(item);
    }
  }

  // Get display value for non-array fields
  getDisplayValue(value: any): string {
    if (value === null || value === undefined || value === '') {
      return 'Empty';
    }
    return value.toString();
  }



  openStatusDialog(item: any): void {
    this.ref = this.dialogService.open(QuotationStatusDialogComponent, {
      header: ' ',
      width: '50%',
      styleClass: 'custom-dialog',
      contentStyle: {
        'max-height': '700px',
        'border-radius': '12px',
        'box-shadow': '0 10px 25px rgba(0,0,0,0.1)'
      },
      baseZIndex: 10000,
      dismissableMask: true,
      data: {
        workflowData: item,
      },
    });

    this.ref.onClose.subscribe((result: any) => {
      if (result) {
        const updatedItems = this.workflowItems.map((workflow) => {
          if (workflow.orderId === item.orderId) {
            return {
              ...workflow,
              status: result.status,
              statusId: result.statusId,
              statusLabel: result.statusLabel
            };
          }
          return workflow;
        });

        this.workflowDataService.updateWorkflowItems(updatedItems);

        this.messageService.add({
          severity: 'success',
          summary: 'Status Updated',
          detail: `${item.quotationNo} is now ${result.statusLabel}`,
          life: 3000,
          closable: true,
          styleClass: 'success-toast'
        });
      }
    });
  }
  // Export selected items (modify to work with complete data)
  exportToExcel(): void {
    if (!this.selectedWorkflowItems || this.selectedWorkflowItems.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'No Selection',
        detail: 'Please select at least one item to export',
        life: 3000
      });
      return;
    }

    this.isGeneratingExcel = true;

    try {
      const exportData = this.prepareDataForExport(this.selectedWorkflowItems);
      this.generateExcelFile(exportData, `selected_enclosure_projects_${new Date().toISOString().split('T')[0]}.xlsx`);

      this.messageService.add({
        severity: 'success',
        summary: 'Success',
        detail: `${this.selectedWorkflowItems.length} records exported successfully`,
        life: 3000
      });
    } catch (error) {
      console.error('Export error:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Export Failed',
        detail: 'Failed to export data to Excel',
        life: 3000
      });
    } finally {
      this.isGeneratingExcel = false;
    }
  }

  // Export ALL items including those without poNumber
  exportAllToExcel(): void {
    if (!this.workflowItems || this.workflowItems.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'No Data',
        detail: 'No data available to export',
        life: 3000
      });
      return;
    }

    this.isGeneratingExcel = true;

    try {
      const exportData = this.prepareDataForExport(this.workflowItems);
      this.generateExcelFile(exportData, `all_enclosure_projects_${new Date().toISOString().split('T')[0]}.xlsx`);

      this.messageService.add({
        severity: 'success',
        summary: 'Success',
        detail: `${this.workflowItems.length} records exported successfully (including items without PO Number)`,
        life: 3000
      });
    } catch (error) {
      console.error('Export error:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Export Failed',
        detail: 'Failed to export data to Excel',
        life: 3000
      });
    } finally {
      this.isGeneratingExcel = false;
    }
  }
  // Add a new function to export ONLY items without poNumber
  exportItemsWithoutPoNumber(): void {
    const itemsWithoutPo = this.workflowItems.filter(item =>
      !item.poNumber ||
      item.poNumber === null ||
      item.poNumber === undefined ||
      item.poNumber === ''
    );

    if (itemsWithoutPo.length === 0) {
      this.messageService.add({
        severity: 'info',
        summary: 'No Data',
        detail: 'No items without PO Number found',
        life: 3000
      });
      return;
    }

    this.isGeneratingExcel = true;

    try {
      const exportData = this.prepareDataForExport(itemsWithoutPo);
      this.generateExcelFile(exportData, `items_without_po_${new Date().toISOString().split('T')[0]}.xlsx`);

      this.messageService.add({
        severity: 'success',
        summary: 'Success',
        detail: `${itemsWithoutPo.length} items without PO Number exported successfully`,
        life: 3000
      });
    } catch (error) {
      console.error('Export error:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Export Failed',
        detail: 'Failed to export data to Excel',
        life: 3000
      });
    } finally {
      this.isGeneratingExcel = false;
    }
  }

  // Get count of items without PO number for display
  getItemsWithoutPoCount(): number {
    if (!this.workflowItems) return 0;

    return this.workflowItems.filter(item =>
      !item.poNumber ||
      item.poNumber === null ||
      item.poNumber === undefined ||
      item.poNumber === ''
    ).length;
  }

  // Export currently filtered items (what user sees)
  exportFilteredToExcel(): void {
    if (!this.filteredWorkflowItems || this.filteredWorkflowItems.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'No Data',
        detail: 'No filtered data available to export',
        life: 3000
      });
      return;
    }

    this.isGeneratingExcel = true;

    try {
      const exportData = this.prepareDataForExport(this.filteredWorkflowItems);
      this.generateExcelFile(exportData, `filtered_enclosure_projects_${new Date().toISOString().split('T')[0]}.xlsx`);

      this.messageService.add({
        severity: 'success',
        summary: 'Success',
        detail: `${this.filteredWorkflowItems.length} filtered records exported successfully`,
        life: 3000
      });
    } catch (error) {
      console.error('Export error:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Export Failed',
        detail: 'Failed to export filtered data to Excel',
        life: 3000
      });
    } finally {
      this.isGeneratingExcel = false;
    }
  }
  prepareDataForExport(workflowItems: any[]): any[] {
    const exportData: any[] = [];

    workflowItems.forEach(workflow => {
      const invoices = workflow.invoices || [];
      const payments = workflow.payments || [];
      const totalPrices = workflow.totalPrices || [];

      // **SIMPLE LOGIC**: Always create rows based on totalPrices length or invoices
      const rowCount = Math.max(totalPrices.length, invoices.length);

      // Create rows for each detail item
      for (let i = 0; i < rowCount; i++) {
        const price = totalPrices[i] || workflow.totalPrice || 0;
        const invoice = invoices[i] || invoices[0] || null;
        const payment = payments.find((p: any) => p.invoiceNo === invoice?.invoiceNo) || payments[i] || null;

        const row = this.createExportRow(workflow, invoice, payment, price, true);
        exportData.push(row);
      }
    });

    return exportData;
  }

  /**
   * **FIXED**: Create export row with proper TypeScript typing
   */
  createExportRow(
    workflow: any,
    invoice: any,
    payment: any,
    valueUSD: number = 0,
    isDetailRow: boolean = true  // Always detail
  ): Record<string, any> {
    const row: Record<string, any> = {
      'Inquiry Received Date': this.formatDateForExcel(workflow.quotationDate),
      'Quotation No': workflow.quotationNo || '',
      'Project Name': workflow.project || '',
      'Customer': workflow.customerName || '',
      'Enclosure Type': workflow.enclosureType || '',
      'Purchase Order (PO) No': workflow.poNumber || '',
      'PO Received Date': this.formatDateForExcel(workflow.poDate),
      'Proforma Invoice (PI) No': workflow.proformaInvoice || '',
      'Value USD': valueUSD,  // ✅ Individual price from totalPrices array
      'Job No.': workflow.jobNumber || '',
      'Invoice No': invoice?.invoiceNo || '',
      'BL Date': this.formatDateForExcel(invoice?.blDate),
      'Payment Due Date': this.formatDateForExcel(invoice?.paymentDueDate),
      'Number of Delayed Days': invoice?.delayedDays || 0,
      'Invoice Date': this.formatDateForExcel(invoice?.date),
      'Invoice Value': invoice?.invoicePrice || 0,
      'Payment Date': this.formatDateForExcel(payment?.paymentReceivedDate),
      'Payment Values': payment?.paymentPrice || 0,
      'Contact': workflow.contact || '',
      'Email': workflow.email || '',
      'PI Sent Date': this.formatDateForExcel(workflow.piDate),
      'Method of Inquiry Received': workflow.methodOfInquiryReceived || '',
      'Country': workflow.country || '',
      'Address': workflow.customerAddress || '',
      'Responsible Person (Engineer)': workflow.engineer || '',
      'Responsible Person (Technical Officer)': workflow.technicalOfficer || '',
    };

    // **SIMPLE**: Only mark as DETAIL
    row['_rowType'] = 'DETAIL';

    return row;
  }


  formatDateForExcel(dateValue: any): Date | string {
    if (!dateValue) return '';

    try {
      const date = new Date(dateValue);
      if (isNaN(date.getTime())) return '';

      return date;
    } catch (error) {
      return '';
    }
  }
  generateExcelFile(data: any[], filename: string): void {
    import('xlsx').then((XLSX) => {
      const worksheet = XLSX.utils.json_to_sheet(data, {
        cellDates: true,
        dateNF: 'yyyy-mm-dd'
      });

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Enclosure Projects');

      const headers = Object.keys(data[0] || {}).filter(h => h !== '_rowType');  // **NEW**: Exclude _rowType

      // Set optimized column widths
      const columnWidths: any[] = [];
      headers.forEach((header, index) => {
        let maxContentLength = 0;

        data.forEach(row => {
          const cellValue = row[header]?.toString() || '';
          maxContentLength = Math.max(maxContentLength, cellValue.length);
        });

        const optimalWidth = this.getOptimalColumnWidth(header, maxContentLength);
        columnWidths[index] = { wch: optimalWidth };
      });

      worksheet['!cols'] = columnWidths;

      // Apply styling - **UPDATED**: Pass data for row type detection
      this.applyExcelStyling(worksheet, headers, data.length, XLSX, data);

      // Enable auto-filters
      worksheet['!autofilter'] = { ref: worksheet['!ref'] || 'A1' };

      XLSX.writeFile(workbook, filename);
    }).catch((error) => {
      console.error('Failed to load XLSX library:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Export Failed',
        detail: 'Failed to load Excel export library',
        life: 3000
      });
    });
  }



  private applyExcelStyling(
    worksheet: any,
    headers: string[],
    dataLength: number,
    XLSX: any,
    data: any[] = []
  ): void {
    const headerStyle = {
      fill: { fgColor: { rgb: "CDC0DA" } },
      font: { bold: true, color: { rgb: "000000" }, sz: 11 },
      alignment: { horizontal: "center", vertical: "center" },
      border: {
        top: { style: "thin", color: { rgb: "000000" } },
        bottom: { style: "thin", color: { rgb: "000000" } },
        left: { style: "thin", color: { rgb: "000000" } },
        right: { style: "thin", color: { rgb: "000000" } }
      }
    };

    const cellStyle = {
      fill: { fgColor: { rgb: "C5D9F1" } },
      font: { color: { rgb: "000000" }, sz: 10 },
      alignment: { horizontal: "left", vertical: "center" },
      border: {
        top: { style: "thin", color: { rgb: "CCCCCC" } },
        bottom: { style: "thin", color: { rgb: "CCCCCC" } },
        left: { style: "thin", color: { rgb: "CCCCCC" } },
        right: { style: "thin", color: { rgb: "CCCCCC" } }
      }
    };

    const dateCellStyle = { ...cellStyle, numFmt: "yyyy-mm-dd" };

    const numberCellStyle = {
      ...cellStyle,
      alignment: { horizontal: "right", vertical: "center" },
      numFmt: "#,##0.00"
    };

    // **NEW**: Summary row style (bold, different background)
    const summaryRowStyle = {
      fill: { fgColor: { rgb: "FFD966" } },  // Yellow background
      font: { color: { rgb: "000000" }, sz: 11, bold: true },
      alignment: { horizontal: "right", vertical: "center" },
      border: {
        top: { style: "medium", color: { rgb: "000000" } },
        bottom: { style: "medium", color: { rgb: "000000" } },
        left: { style: "thin", color: { rgb: "000000" } },
        right: { style: "thin", color: { rgb: "000000" } }
      }
    };

    // Apply header styling
    headers.forEach((header, colIndex) => {
      const cellAddress = XLSX.utils.encode_cell({ r: 0, c: colIndex });
      if (!worksheet[cellAddress]) worksheet[cellAddress] = {};
      worksheet[cellAddress].s = headerStyle;
    });

    // Apply data cell styling
    for (let rowIndex = 1; rowIndex <= dataLength; rowIndex++) {
      const rowData = data[rowIndex - 1];  // **NEW**: Get row data
      const isSummaryRow = rowData?._rowType === 'SUMMARY';  // **NEW**: Check if summary

      headers.forEach((header, colIndex) => {
        const cellAddress = XLSX.utils.encode_cell({ r: rowIndex, c: colIndex });
        if (!worksheet[cellAddress]) worksheet[cellAddress] = {};

        const isDateColumn = header.toLowerCase().includes('date');
        const isNumberColumn = header.toLowerCase().includes('value') ||
          header.toLowerCase().includes('price') ||
          header.toLowerCase().includes('usd') ||
          header.toLowerCase().includes('days');

        // **NEW**: Apply summary row style if applicable
        if (isSummaryRow) {
          worksheet[cellAddress].s = summaryRowStyle;
        } else if (isDateColumn) {
          worksheet[cellAddress].s = dateCellStyle;
        } else if (isNumberColumn) {
          worksheet[cellAddress].s = numberCellStyle;
        } else {
          worksheet[cellAddress].s = cellStyle;
        }
      });
    }

    // Set row heights
    const rowHeights = [];
    for (let i = 0; i <= dataLength; i++) {
      if (i === 0) {
        rowHeights[i] = { hpt: 20 };
      } else {
        // **NEW**: Taller height for summary rows
        const rowData = data[i - 1];
        const isSummaryRow = rowData?._rowType === 'SUMMARY';
        rowHeights[i] = { hpt: isSummaryRow ? 18 : 16 };
      }
    }
    worksheet['!rows'] = rowHeights;
  }

  private getOptimalColumnWidth(header: string, maxContentLength: number): number {
    const headerLength = header.length;

    const widthRules: { [key: string]: number } = {
      'date': 12,
      'no': 15,
      'number': 10,
      'name': 25,
      'project': 25,
      'customer': 20,
      'address': 35,
      'email': 25,
      'contact': 15,
      'country': 15,
      'value': 12,
      'usd': 12,
      'price': 12,
      'person': 20,
      'engineer': 20,
      'officer': 20,
      'method': 20,
      'type': 15,
      'invoice': 15,
      'payment': 12,
      'job': 15,
      'proforma': 15,
      'days': 8
    };

    const lowerHeader = header.toLowerCase();
    for (const [key, width] of Object.entries(widthRules)) {
      if (lowerHeader.includes(key)) {
        return width;
      }
    }

    const calculatedWidth = Math.max(headerLength, maxContentLength) + 2;
    return Math.min(calculatedWidth, 30);
  }
  // Drawing Stats - Use filteredWorkflowItems directly (ALREADY FILTERED!)
  getDrawingPoTotal(): number {
    return this.filteredWorkflowItems.reduce((total, item) =>
      total + (parseFloat(item.totalPrice || '0') || 0), 0);
  }

  getDrawingInvoiceTotal(): number {
    return this.filteredWorkflowItems.reduce((total, item) =>
      total + (item.totalInvoiceAmount || 0), 0);
  }

  getDrawingPaymentTotal(): number {
    return this.filteredWorkflowItems.reduce((total, item) =>
      total + (item.totalPaymentAmount || 0), 0);
  }

  // Encloser Stats - Use filteredWorkflowItems directly (ALREADY FILTERED!)
  getEncloserPoTotal(): number {
    return this.filteredWorkflowItems.reduce((total, item) =>
      total + (parseFloat(item.totalPrice || '0') || 0), 0);
  }

  getEncloserInvoiceTotal(): number {
    return this.filteredWorkflowItems.reduce((total, item) =>
      total + (item.totalInvoiceAmount || 0), 0);
  }

  getEncloserPaymentTotal(): number {
    return this.filteredWorkflowItems.reduce((total, item) =>
      total + (item.totalPaymentAmount || 0), 0);
  }


}